import { Table, SortTH } from 'foundations-components/transitional/components/Table';
import { useRef, useState } from 'react';
import { useTheme } from 'foundations-theming/utils';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import UserAvatarLink from '../common/UserAvatarLink';
import { Small } from 'foundations-components/transitional/components/Small';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PlaylistLink from './PlaylistLink';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import { usePlaylistAssignments } from '../../queries/playlistQueries';
import ACTableSkeleton from 'academy-common-lib/components/loading/ACTableSkeleton';
import { NavMarker } from 'react-rhumb';
import ApiError from 'academy-common-lib/components/error/ApiError';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import MyTeamTablePagination from '../common/TablePagination';
import { useTableSorting } from '../../hooks/tableHooks';
import { getOffsetPosition, getPaginatedPosition } from '../../utils/paginationUtils';
import EmptyTable from '../common/EmptyTable';
import { useParams } from 'react-router-dom';
import { getPlaylistTypeLabel, getProgressPercent, getStatus } from '../../utils/playlistsUtils';
import { useUserInfo } from '../../../accounts/accountsQueries';
import PortalIdParser from 'PortalIdParser';
import { Button } from 'foundations-components/transitional/components/Button';
import { PlaylistAssignPanel } from '../../../playlistAssignments/components/PlaylistAssignPanel';
import { dueDateOptions, completedDateOptions } from '../../constants/filters';
import { getStartOfDay, getEndOfDay } from '../../utils/dateTimeUtils';
import { usePlaylistsTableStore } from '../../store/myTeamTableStateStore';
import DateTableCell from '../common/DateTableCell';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const buildDateRange = (selected, options) => {
  if (!selected) return undefined;
  const {
    startDate,
    endDate
  } = options[selected].getValue();
  return {
    startDate: getStartOfDay(startDate),
    endDate: getEndOfDay(endDate)
  };
};
const PlaylistsTableForStatus = ({
  status,
  tableName
}) => {
  var _playlistAssignments$;
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const portalId = PortalIdParser.get();
  const {
    userInfo
  } = useUserInfo();
  const isAllTab = status === undefined;
  const theme = useTheme();
  const {
    isDesktop
  } = useMediaQuery();
  const store = usePlaylistsTableStore(tableName);
  const {
    state: {
      page,
      pageSize,
      sortBy,
      sortOrder,
      search,
      teams,
      users,
      assignedByMe,
      playlists,
      dueDate,
      completedDate
    },
    actions: {
      setPage,
      setPageSize,
      setSortBy,
      setSortOrder
    }
  } = store;
  const {
    handleSortChange,
    getSortDirectionForColumn
  } = useTableSorting({
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder
  });
  const offsetPosition = getOffsetPosition(page, pageSize);
  const {
    playlistAssignments,
    playlistAssignmentsLoading,
    playlistAssignmentsError
  } = usePlaylistAssignments({
    pageLength: pageSize,
    offset: offsetPosition,
    sortOrder,
    sortField: sortBy,
    playlistNameQuery: search,
    teams,
    users,
    playlists,
    status,
    assignedBy: assignedByMe ? userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.id : undefined,
    dueDate: buildDateRange(dueDate, dueDateOptions),
    completedAt: buildDateRange(completedDate, completedDateOptions)
  });
  const totalResultsRef = useRef(0);
  if (playlistAssignmentsError) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "MY_TEAM_PLAYLISTS_TABLE_API_ERROR",
      children: /*#__PURE__*/_jsx(ApiError, {
        error: {
          status: 500
        }
      })
    });
  }
  if (!playlistAssignmentsLoading && playlistAssignments) {
    totalResultsRef.current = playlistAssignments.total;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [playlistAssignmentsLoading ? /*#__PURE__*/_jsx(ACTableSkeleton, {
      columns: isAllTab ? 5 : 4,
      condensed: true,
      rows: pageSize
    }) : playlistAssignments !== null && playlistAssignments !== void 0 && (_playlistAssignments$ = playlistAssignments.results) !== null && _playlistAssignments$ !== void 0 && _playlistAssignments$.length ? /*#__PURE__*/_jsx(NavMarker, {
      name: "MY_TEAM_PLAYLISTS_TABLE_LOADED",
      children: /*#__PURE__*/_jsxs(Table, {
        condensed: true,
        "data-test-id": "playlists-table",
        hover: true,
        responsive: true,
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {
            style: {
              width: '35%',
              borderInlineEnd: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '20%'
            }
          }), status !== 'COMPLETED' && /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          }), status !== 'INCOMPLETE' && /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          })]
        }), /*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('PLAYLIST_NAME'),
              sort: getSortDirectionForColumn('PLAYLIST_NAME'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.playlists.tableColumns.playlist"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('TEAM_MEMBER_NAME'),
              sort: getSortDirectionForColumn('TEAM_MEMBER_NAME'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.playlists.tableColumns.name"
              })
            }), status !== 'COMPLETED' && /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('PROGRESS'),
              sort: getSortDirectionForColumn('PROGRESS'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.playlists.tableColumns.progress"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('DUE_DATE'),
              sort: getSortDirectionForColumn('DUE_DATE'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.playlists.tableColumns.dueDate"
              })
            }), status !== 'INCOMPLETE' && /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('COMPLETED_DATE'),
              sort: getSortDirectionForColumn('COMPLETED_DATE'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.playlists.tableColumns.completed"
              })
            })]
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: playlistAssignments.results.map((playlistAssignment, index) => {
            const progressPercent = getProgressPercent(playlistAssignment.playlistProgress);
            const position = getPaginatedPosition(offsetPosition, index);
            return /*#__PURE__*/_jsxs("tr", {
              "data-test-id": "playlists-table-data-row",
              children: [/*#__PURE__*/_jsxs("td", {
                "data-test-id": "playlist-cell",
                children: [/*#__PURE__*/_jsx(PlaylistLink, {
                  playlistId: playlistAssignment.playlistId,
                  playlistName: playlistAssignment.playlistName,
                  portalId: portalId,
                  position: position
                }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(Small, {
                  children: getPlaylistTypeLabel(readUnhandledEnumCase(playlistAssignment.playlistType))
                })]
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "user-cell",
                children: /*#__PURE__*/_jsx(UserAvatarLink, {
                  displayAvatar: isDesktop,
                  position: position,
                  userEmail: playlistAssignment.userEmail,
                  userId: playlistAssignment.userId,
                  userName: playlistAssignment.userName
                })
              }), status !== 'COMPLETED' && /*#__PURE__*/_jsx("td", {
                "data-test-id": "progress-cell",
                children: /*#__PURE__*/_jsx(FormattedPercentage, {
                  value: progressPercent
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "due-date-cell",
                children: /*#__PURE__*/_jsx(Button, {
                  onClick: () => {
                    setSelectedPlaylist({
                      playlistId: playlistAssignment.playlistId,
                      assignmentId: playlistAssignment.assignmentId
                    });
                  },
                  use: "link",
                  children: playlistAssignment.dueDate ? /*#__PURE__*/_jsx(FormattedDateTime, {
                    format: "ll",
                    type: "utc",
                    value: playlistAssignment.dueDate
                  }) : /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.myTeam.playlists.addDueDate"
                  })
                })
              }), status !== 'INCOMPLETE' && /*#__PURE__*/_jsx(DateTableCell, {
                testId: "completed-date-cell",
                value: playlistAssignment.completedDate
              })]
            }, `${playlistAssignment.playlistId}-${playlistAssignment.userId}`);
          })
        })]
      })
    }) : /*#__PURE__*/_jsx(EmptyTable, {
      navMarkerName: "MY_TEAM_PLAYLISTS_TABLE_EMPTY"
    }), /*#__PURE__*/_jsx(MyTeamTablePagination, {
      onPageChange: setPage,
      onPageSizeChange: setPageSize,
      page: page,
      pageSize: pageSize,
      totalResults: totalResultsRef.current
    }), selectedPlaylist && portalId && /*#__PURE__*/_jsx(PlaylistAssignPanel, {
      assignmentIds: [selectedPlaylist.assignmentId],
      onClose: () => setSelectedPlaylist(null),
      onSuccess: () => setSelectedPlaylist(null),
      playlistId: selectedPlaylist.playlistId,
      portalId: portalId
    })]
  });
};
const PlaylistsTable = ({
  tableName
}) => {
  const {
    playlistStatus
  } = useParams();
  const status = getStatus(playlistStatus);
  return /*#__PURE__*/_jsx(PlaylistsTableForStatus, {
    status: status,
    tableName: tableName
  }, status);
};
export default PlaylistsTable;